import type {
  DeclaredAnswerInput,
  DeviceInspectionSummary,
  InspectionFieldSummary,
  PublicDeclaredField,
  QuotationAnswerSummary,
} from '@/features/operations/types';

/**
 * Reading and comparing the condition of a received phone (#89).
 *
 * Separate from the components that render it: both the reception form and the sheet's edit
 * use these, and a module that exports components and helpers together is one Vite cannot
 * hot-reload cleanly.
 */

/// The condition as it is being typed: the option id for a choice, the typed number for a
/// percentage. One empty string means "not observed" for both.
export type ConditionDraft = Record<string, string>;

/// What each side says about one question.
export type ConditionDifference = {
  fieldId: string;
  fieldLabel: string;
  declaredLabel: string | null;
  observedLabel: string | null;
};

/**
 * The questions a phone with no request of its own is inspected against: the current form's
 * screen, casing and battery fields. A phone received against a request uses that request's
 * `inspectionFields` instead, so its declared side can be compared.
 */
export function buildInspectionFields(fields: PublicDeclaredField[]): InspectionFieldSummary[] {
  return fields
    .filter((field) =>
      ['Estado de la pantalla', 'Estado de la carcasa', 'Salud de la batería'].includes(
        field.label,
      ),
    )
    .map((field) => ({
      fieldId: field.id,
      fieldLabel: field.label,
      fieldType: field.type,
      options: field.options.map((option) => ({ ...option, isActive: true })),
    }));
}

export function findAnswer(answers: QuotationAnswerSummary[], fieldId: string) {
  return answers.find((answer) => answer.fieldId === fieldId) || null;
}

/**
 * The questions the condition section asks: the ones answered by choosing an option.
 *
 * Battery is deliberately not among them. It is an integer on the sheet -- the phone's battery
 * percentage, one more field beside the serial number and the colour -- not a separate observation, and
 * asking it twice in one form is what the merged sheet made obvious.
 *
 * This assumes the percentage questions PhoneTec configures are about the battery. If it ever
 * configures a second one, it will have nowhere to be answered, and this is where that shows.
 */
export function conditionQuestions(fields: InspectionFieldSummary[]) {
  return fields.filter((field) => field.fieldType === 'SINGLE_CHOICE');
}

/** What the applicant declared about the battery, to compare the measured one against. */
export function declaredBattery(declared: QuotationAnswerSummary[]) {
  return declared.find((answer) => answer.fieldType === 'PERCENTAGE') || null;
}

export function valueOf(answer: QuotationAnswerSummary | null, field: InspectionFieldSummary) {
  if (!answer) {
    return '';
  }

  return field.fieldType === 'SINGLE_CHOICE'
    ? (answer.optionId ?? '')
    : answer.percentValue === null
      ? ''
      : String(answer.percentValue);
}

export function labelFor(field: InspectionFieldSummary, value: string) {
  if (!value) {
    return null;
  }

  if (field.fieldType !== 'SINGLE_CHOICE') {
    return `${value}%`;
  }

  const option = field.options.find((candidate) => candidate.id === value);
  return option ? option.label : value;
}

/**
 * The condition as it was last recorded on the device, for the sheet's edit. A device with no
 * recorded condition starts with every question "Sin observar".
 */
export function buildConditionDraft(
  fields: InspectionFieldSummary[],
  inspection: DeviceInspectionSummary | null,
): ConditionDraft {
  const draft: ConditionDraft = {};

  conditionQuestions(fields).forEach((field) => {
    draft[field.fieldId] = inspection
      ? valueOf(findAnswer(inspection.answers, field.fieldId), field)
      : '';
  });

  return draft;
}

/**
 * The condition as the applicant declared it: where reception starts (ADR 0005 c.1, #182).
 *
 * The employee verifies the declaration with the phone in hand and corrects only what differs.
 * Saving without touching a question records it as declared, and what was changed is still
 * named against the declaration -- at save time and on the sheet -- so the comparison survives.
 */
export function buildDeclaredConditionDraft(
  fields: InspectionFieldSummary[],
  declared: QuotationAnswerSummary[],
): ConditionDraft {
  const draft: ConditionDraft = {};

  conditionQuestions(fields).forEach((field) => {
    draft[field.fieldId] = valueOf(findAnswer(declared, field.fieldId), field);
  });

  return draft;
}

/** The battery percentage the applicant declared, as the reception form's starting value. */
export function declaredBatteryInput(declared: QuotationAnswerSummary[]) {
  const percent = declaredBattery(declared)?.percentValue;
  return percent === null || percent === undefined ? '' : String(percent);
}

export function conditionAnswers(
  fields: InspectionFieldSummary[],
  draft: ConditionDraft,
): DeclaredAnswerInput[] {
  return conditionQuestions(fields)
    .filter((field) => Boolean(draft[field.fieldId]))
    .map((field) => ({ fieldId: field.fieldId, optionId: draft[field.fieldId] }));
}

/**
 * What is about to be saved that contradicts the declaration.
 *
 * Covers the measured battery too, which lives on the sheet rather than among these questions:
 * it is the value most likely to disagree with what somebody declared from memory, so leaving
 * it out of the confirmation would defeat the point of the step.
 *
 * A question nobody observed is not a difference: leaving it blank says "not checked", which
 * is not the same as disagreeing with the applicant.
 */
export function conditionDifferences(
  fields: InspectionFieldSummary[],
  declared: QuotationAnswerSummary[],
  draft: ConditionDraft,
  battery?: string,
): ConditionDifference[] {
  const differences = conditionQuestions(fields)
    .filter((field) => {
      const value = draft[field.fieldId] || '';
      return Boolean(value) && value !== valueOf(findAnswer(declared, field.fieldId), field);
    })
    .map((field) => ({
      fieldId: field.fieldId,
      fieldLabel: field.fieldLabel,
      declaredLabel: findAnswer(declared, field.fieldId)?.answerLabel ?? null,
      observedLabel: labelFor(field, draft[field.fieldId] || ''),
    }));

  const declaredPercent = declaredBattery(declared);

  if (battery && declaredPercent && Number(battery) !== declaredPercent.percentValue) {
    differences.push({
      fieldId: declaredPercent.fieldId,
      fieldLabel: declaredPercent.fieldLabel,
      declaredLabel: declaredPercent.answerLabel,
      observedLabel: `${battery}%`,
    });
  }

  return differences;
}
